<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import UserPreferencesAPI from 'dashboard/api/userPreferences';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], default: null },
});

const store = useStore();
const { t } = useI18n();
const isLoading = ref(false);
const showCreateForm = ref(false);
const expandedPipelines = ref({});

// Deal status filter — persisted per contact on the server
const dealStatusFilter = ref('open');
let saveTimeout = null;

const loadFilterPreference = async contactId => {
  try {
    const { data } = await UserPreferencesAPI.get({
      key: 'deal_status_filter',
      resourceType: 'Contact',
      resourceId: contactId,
    });
    if (data.value) dealStatusFilter.value = data.value;
  } catch {
    dealStatusFilter.value = 'open';
  }
};

const saveFilterPreference = (contactId, value) => {
  clearTimeout(saveTimeout);
  saveTimeout = setTimeout(async () => {
    try {
      await UserPreferencesAPI.set({
        key: 'deal_status_filter',
        value,
        resourceType: 'Contact',
        resourceId: contactId,
      });
    } catch {
      /* silent */
    }
  }, 300);
};

// Create form (must be declared before computeds using it)
const createForm = ref({
  product_id: null,
  pipeline_id: null,
  priority: 'medium',
  value: 0,
  title: '',
});

const pipelines = computed(() => store.getters['kanban/getActivePipelines']);
const allProducts = computed(() => store.getters['kanban/getActiveProducts']);

const getStagesByPipeline = pipelineId => {
  const getter = store.getters['kanban/getStagesByPipeline'];
  if (typeof getter === 'function') {
    const stages = getter(pipelineId);
    if (stages && stages.length > 0) return stages;
  }
  const pipeline = pipelines.value.find(
    p => Number(p.id) === Number(pipelineId)
  );
  return pipeline?.stages || [];
};

const getPipelineForDeal = deal =>
  pipelines.value.find(
    pipeline => Number(pipeline.id) === Number(deal.stage?.pipeline_id)
  ) ||
  pipelines.value.find(pipeline =>
    getStagesByPipeline(pipeline.id)?.some(
      stage => Number(stage.id) === Number(deal.stage_id)
    )
  ) ||
  null;

const getStagesForDeal = deal => {
  const pipeline = getPipelineForDeal(deal);
  return pipeline ? getStagesByPipeline(pipeline.id) : [];
};

const getStageProgress = deal => {
  const stages = getStagesForDeal(deal);
  if (!stages.length) return 0;
  const sorted = [...stages].sort((a, b) => a.position - b.position);
  const idx = sorted.findIndex(s => Number(s.id) === Number(deal.stage_id));
  return idx >= 0 ? ((idx + 1) / sorted.length) * 100 : 0;
};

const deals = computed(() =>
  store.getters['kanban/getDealsByContact'](Number(props.contactId))
);

// Filter products by selected pipeline (empty pipeline_ids = available everywhere)
const filteredProducts = computed(() => {
  const pipelineId = createForm.value.pipeline_id;
  if (!pipelineId) return allProducts.value;
  return allProducts.value.filter(
    p =>
      !p.pipeline_ids ||
      p.pipeline_ids.length === 0 ||
      p.pipeline_ids.includes(pipelineId)
  );
});

// Filter deals by status
const filteredDeals = computed(() => {
  if (dealStatusFilter.value === 'all') return deals.value;
  return deals.value.filter(d => d.status === dealStatusFilter.value);
});

// Group deals by pipeline
const dealsByPipeline = computed(() => {
  const grouped = {};
  filteredDeals.value.forEach(deal => {
    const pipeline = getPipelineForDeal(deal);
    const pipelineId = pipeline?.id || 0;
    if (!grouped[pipelineId]) {
      grouped[pipelineId] = {
        pipeline,
        name: pipeline?.name || 'Sem Pipeline',
        deals: [],
      };
    }
    grouped[pipelineId].deals.push(deal);
  });
  return Object.values(grouped);
});

const selectedPipelineStages = computed(() => {
  if (!createForm.value.pipeline_id) return [];
  return getStagesByPipeline(createForm.value.pipeline_id);
});

watch(
  () => props.contactId,
  id => {
    loadFilterPreference(id);
  }
);
watch(dealStatusFilter, val => {
  saveFilterPreference(props.contactId, val);
});

const fetchDeals = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('kanban/fetchDeals', { contact_id: props.contactId });
  } finally {
    isLoading.value = false;
  }
};

const fetchPipelineStages = async pipelineId => {
  await store.dispatch('kanban/fetchStages', pipelineId);
  await store.dispatch('kanban/fetchPipelines');
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('kanban/fetchPipelines'),
    store.dispatch('kanban/fetchProducts', { active_only: true }),
    loadFilterPreference(props.contactId),
  ]);
  await fetchDeals();
  pipelines.value.forEach(p => {
    expandedPipelines.value[p.id] = true;
  });
});

watch(() => props.contactId, fetchDeals);

const togglePipeline = id => {
  expandedPipelines.value[id] = !expandedPipelines.value[id];
};

const openCreateForm = (pipelineId = null) => {
  const targetPipeline = pipelineId
    ? pipelines.value.find(p => p.id === pipelineId)
    : pipelines.value[0];
  createForm.value = {
    product_id: null,
    pipeline_id: targetPipeline?.id || null,
    priority: 'medium',
    value: 0,
    title: '',
  };
  if (targetPipeline) fetchPipelineStages(targetPipeline.id);
  showCreateForm.value = true;
};

const onProductChange = () => {
  const product = allProducts.value.find(
    p => p.id === createForm.value.product_id
  );
  if (product) {
    createForm.value.title = product.name;
    createForm.value.value = product.default_value || 0;
  }
};

const onPipelineChange = async () => {
  if (createForm.value.pipeline_id)
    await fetchPipelineStages(createForm.value.pipeline_id);
  // Reset product if it's no longer available in the new pipeline
  if (createForm.value.product_id) {
    const stillAvailable = filteredProducts.value.find(
      p => p.id === createForm.value.product_id
    );
    if (!stillAvailable) createForm.value.product_id = null;
  }
};

const createDeal = async () => {
  const stages = selectedPipelineStages.value;
  const firstStage =
    stages.length > 0
      ? [...stages].sort((a, b) => a.position - b.position)[0]
      : null;
  if (!firstStage) return;

  const selectedPipeline = pipelines.value.find(
    p => p.id === createForm.value.pipeline_id
  );
  try {
    await store.dispatch('kanban/createDeal', {
      title:
        createForm.value.title ||
        `${selectedPipeline?.name || 'Oportunidade'} - Novo`,
      value: createForm.value.value,
      priority: createForm.value.priority,
      stage_id: firstStage.id,
      contact_id: Number(props.contactId),
      conversation_id: props.conversationId
        ? Number(props.conversationId)
        : null,
      product_id: createForm.value.product_id,
    });
    showCreateForm.value = false;
    await fetchDeals();
  } catch (error) {
    /* handled by store */
  }
};

const formatCurrency = (value, dealCurrency) => {
  const locale = navigator.language || 'pt-BR';
  let currencyCode = dealCurrency;
  if (!currencyCode) {
    const lang = locale.split('-')[0].toLowerCase();
    if (lang === 'pt') {
      currencyCode = 'BRL';
    } else if (['es', 'fr', 'it', 'de'].includes(lang)) {
      currencyCode = 'EUR';
    } else {
      currencyCode = 'USD';
    }
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  }).format(value);
};

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const locale = navigator.language || 'pt-BR';
  return new Date(dateStr).toLocaleDateString(locale);
};

const priorityConfig = {
  low: {
    key: 'KANBAN.PRIORITY.LOW',
    class:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  medium: {
    key: 'KANBAN.PRIORITY.MEDIUM',
    class:
      'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  high: {
    key: 'KANBAN.PRIORITY.HIGH',
    class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
};

const statusConfig = {
  open: {
    key: 'KANBAN.STATUS.OPEN',
    icon: 'i-lucide-circle-dot',
    class: 'text-blue-500',
  },
  won: {
    key: 'KANBAN.STATUS.WON',
    icon: 'i-lucide-check-circle-2',
    class: 'text-emerald-500',
  },
  lost: {
    key: 'KANBAN.STATUS.LOST',
    icon: 'i-lucide-x-circle',
    class: 'text-red-500',
  },
};

const onStageChange = async (deal, stageId) => {
  await store.dispatch('kanban/moveDeal', {
    dealId: deal.id,
    stageId: Number(stageId),
  });
};

const markWon = async deal => {
  await store.dispatch('kanban/wonDeal', deal.id);
};

const markLost = async deal => {
  // eslint-disable-next-line no-alert
  const reason = window.prompt(t('KANBAN.DEAL_MODAL.LOST_REASON_PROMPT'));
  await store.dispatch('kanban/lostDeal', {
    dealId: deal.id,
    lostReason: reason || '',
  });
};

const removeDeal = async deal => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (
    !window.confirm(t('KANBAN.SIDEBAR.REMOVE_CONFIRM', { title: deal.title }))
  )
    return;
  await store.dispatch('kanban/deleteDeal', deal.id);
  await fetchDeals();
};
</script>

<template>
  <div class="space-y-2">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-6">
      <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-10" />
    </div>

    <template v-else>
      <!-- Summary bar -->
      <div class="flex items-center justify-between px-1">
        <div class="flex items-center gap-1.5">
          <span class="text-[10px] text-n-slate-10">
            {{ filteredDeals.length }}
            {{
              filteredDeals.length === 1
                ? t('KANBAN.SIDEBAR.OPPORTUNITY_SINGULAR')
                : t('KANBAN.SIDEBAR.OPPORTUNITY_PLURAL')
            }}
          </span>
          <select
            v-model="dealStatusFilter"
            class="text-[10px] px-1.5 py-0.5 rounded-md border border-n-weak bg-n-surface-2 text-n-slate-11 outline-none focus:ring-1 focus:ring-n-brand cursor-pointer"
          >
            <option value="open">{{ t('KANBAN.FILTER.OPEN') }}</option>
            <option value="won">{{ t('KANBAN.FILTER.WON') }}</option>
            <option value="lost">{{ t('KANBAN.FILTER.LOST') }}</option>
            <option value="all">{{ t('KANBAN.FILTER.ALL') }}</option>
          </select>
        </div>
        <button
          class="inline-flex items-center gap-1 text-[10px] font-medium text-n-brand hover:text-n-brand/80 transition-colors"
          @click="openCreateForm()"
        >
          <span class="i-lucide-plus size-3" />
          {{ t('KANBAN.SIDEBAR.BTN_NEW') }}
        </button>
      </div>

      <!-- Create Form -->
      <div
        v-if="showCreateForm"
        class="rounded-xl border border-n-brand/30 bg-n-surface-1 overflow-hidden"
      >
        <div class="px-3 py-2 border-b border-n-weak bg-n-brand/5">
          <span class="text-xs font-semibold text-n-brand">{{
            t('KANBAN.SIDEBAR.NEW_OPPORTUNITY')
          }}</span>
        </div>
        <form class="px-3 py-2.5 space-y-2" @submit.prevent="createDeal">
          <!-- Pipeline -->
          <div>
            <label
              class="block text-[10px] font-medium text-n-slate-10 mb-0.5 uppercase tracking-wider"
              >{{ t('KANBAN.SIDEBAR.FIELD_PIPELINE') }}</label
            >
            <select
              v-model="createForm.pipeline_id"
              class="w-full text-xs px-2 py-1.5 rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
              @change="onPipelineChange"
            >
              <option v-for="p in pipelines" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
          </div>
          <!-- Product selector -->
          <div>
            <label
              class="block text-[10px] font-medium text-n-slate-10 mb-0.5 uppercase tracking-wider"
              >{{ t('KANBAN.SIDEBAR.FIELD_PRODUCT') }}</label
            >
            <select
              v-model="createForm.product_id"
              class="w-full text-xs px-2 py-1.5 rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
              @change="onProductChange"
            >
              <option :value="null">
                {{ t('KANBAN.SIDEBAR.NO_PRODUCT') }}
              </option>
              <option
                v-for="prod in filteredProducts"
                :key="prod.id"
                :value="prod.id"
              >
                {{ prod.name }}
                {{
                  prod.category === 'servico'
                    ? t('KANBAN.SIDEBAR.PRODUCT_SERVICE')
                    : t('KANBAN.SIDEBAR.PRODUCT_PRODUCT')
                }}
              </option>
            </select>
          </div>
          <!-- Title (auto-filled from product, editable) -->
          <div>
            <label
              class="block text-[10px] font-medium text-n-slate-10 mb-0.5 uppercase tracking-wider"
              >{{ t('KANBAN.SIDEBAR.FIELD_TITLE') }}</label
            >
            <input
              v-model="createForm.title"
              type="text"
              :placeholder="t('KANBAN.SIDEBAR.PLACEHOLDER_TITLE')"
              class="w-full text-xs px-2 py-1.5 rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:ring-1 focus:ring-n-brand"
            />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label
                class="block text-[10px] font-medium text-n-slate-10 mb-0.5 uppercase tracking-wider"
                >{{ t('KANBAN.SIDEBAR.FIELD_VALUE') }}</label
              >
              <input
                v-model.number="createForm.value"
                type="number"
                step="0.01"
                min="0"
                class="w-full text-xs px-2 py-1.5 rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
            <div>
              <label
                class="block text-[10px] font-medium text-n-slate-10 mb-0.5 uppercase tracking-wider"
                >{{ t('KANBAN.SIDEBAR.FIELD_PRIORITY') }}</label
              >
              <select
                v-model="createForm.priority"
                class="w-full text-xs px-2 py-1.5 rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
              >
                <option value="low">{{ t('KANBAN.PRIORITY.LOW') }}</option>
                <option value="medium">
                  {{ t('KANBAN.PRIORITY.MEDIUM') }}
                </option>
                <option value="high">{{ t('KANBAN.PRIORITY.HIGH') }}</option>
              </select>
            </div>
          </div>
          <div class="flex justify-end gap-1.5 pt-1">
            <button
              type="button"
              class="px-2.5 py-1 text-[11px] text-n-slate-11 rounded-md hover:bg-n-alpha-2"
              @click="showCreateForm = false"
            >
              {{ t('KANBAN.SIDEBAR.BTN_CANCEL') }}
            </button>
            <button
              type="submit"
              :disabled="!createForm.pipeline_id"
              class="px-2.5 py-1 text-[11px] font-medium text-white bg-n-brand rounded-md hover:bg-n-brand/90 disabled:opacity-50"
            >
              {{ t('KANBAN.SIDEBAR.BTN_CREATE') }}
            </button>
          </div>
        </form>
      </div>

      <!-- Empty State -->
      <div
        v-if="deals.length === 0 && !showCreateForm"
        class="text-center py-4"
      >
        <span
          class="i-lucide-briefcase size-8 text-n-slate-8 mx-auto block mb-2"
        />
        <p class="text-xs text-n-slate-10 mb-3">
          {{ t('KANBAN.SIDEBAR.EMPTY_TITLE') }}
        </p>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors"
          @click="openCreateForm()"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('KANBAN.SIDEBAR.EMPTY_BTN') }}
        </button>
      </div>

      <!-- Deals grouped by Pipeline -->
      <div
        v-for="group in dealsByPipeline"
        :key="group.pipeline?.id || 0"
        class="rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden"
      >
        <!-- Pipeline Header -->
        <button
          class="w-full flex items-center gap-2 px-3 py-2 border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
          @click="togglePipeline(group.pipeline?.id)"
        >
          <div
            class="w-2 h-2 rounded-full shrink-0"
            :style="{ backgroundColor: group.pipeline?.color_hex || '#6366f1' }"
          />
          <span
            class="text-xs font-semibold text-n-slate-12 flex-1 text-left"
            >{{ group.name }}</span
          >
          <span
            class="text-[10px] bg-n-alpha-2 text-n-slate-11 px-1.5 py-0.5 rounded-full"
            >{{ group.deals.length }}</span
          >
          <span
            class="i-lucide-chevron-down size-3.5 text-n-slate-10 transition-transform duration-200"
            :class="{ 'rotate-180': !expandedPipelines[group.pipeline?.id] }"
          />
        </button>

        <div v-if="expandedPipelines[group.pipeline?.id]">
          <div
            v-for="(deal, idx) in group.deals"
            :key="deal.id"
            class="px-3 py-2.5 group"
            :class="{ 'border-t border-n-weak/30': idx > 0 }"
          >
            <!-- Title + Product Badge + Priority + Delete -->
            <div class="flex items-center justify-between gap-1.5 mb-1">
              <div class="flex items-center gap-1.5 min-w-0">
                <span
                  :class="[
                    (statusConfig[deal.status] || statusConfig.open).icon,
                    (statusConfig[deal.status] || statusConfig.open).class,
                  ]"
                  class="size-3 shrink-0"
                />
                <span class="text-xs font-semibold text-n-slate-12 truncate">{{
                  deal.title
                }}</span>
              </div>
              <div class="flex items-center gap-1 shrink-0">
                <span
                  :class="
                    (priorityConfig[deal.priority] || priorityConfig.medium)
                      .class
                  "
                  class="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
                >
                  {{
                    t(
                      (priorityConfig[deal.priority] || priorityConfig.medium)
                        .key
                    )
                  }}
                </span>
                <button
                  class="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
                  :title="t('KANBAN.SIDEBAR.REMOVE_TOOLTIP')"
                  @click="removeDeal(deal)"
                >
                  <span class="i-lucide-trash-2 size-3 text-red-500" />
                </button>
              </div>
            </div>

            <!-- Product badge -->
            <div v-if="deal.product" class="flex items-center gap-1 mb-1">
              <span class="i-lucide-package size-2.5 text-n-slate-10" />
              <span class="text-[10px] text-n-slate-10">{{
                deal.product.name
              }}</span>
              <span
                class="text-[9px] bg-n-alpha-2 text-n-slate-10 px-1 py-0.5 rounded"
              >
                {{
                  deal.product.category === 'servico'
                    ? t('KANBAN.SIDEBAR.CATEGORY_SERVICE')
                    : t('KANBAN.SIDEBAR.CATEGORY_PRODUCT')
                }}
              </span>
            </div>

            <!-- Value -->
            <div class="text-sm font-bold text-n-slate-12 mb-1.5">
              {{ formatCurrency(deal.value || 0, deal.currency) }}
            </div>

            <!-- Progress -->
            <div class="space-y-1 mb-2">
              <div
                class="flex items-center justify-between text-[10px] text-n-slate-10"
              >
                <span>{{ deal.stage?.name }}</span>
                <span>{{ Math.round(getStageProgress(deal)) }}%</span>
              </div>
              <div class="w-full h-1 bg-n-alpha-2 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-300"
                  :style="{
                    width: `${getStageProgress(deal)}%`,
                    backgroundColor:
                      deal.status === 'won'
                        ? '#10b981'
                        : deal.status === 'lost'
                          ? '#ef4444'
                          : deal.stage?.color_hex || '#6366f1',
                  }"
                />
              </div>
              <select
                v-if="deal.status === 'open'"
                :value="deal.stage_id"
                class="w-full text-[10px] px-1.5 py-1 rounded border border-n-weak bg-n-surface-2 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
                @change="e => onStageChange(deal, e.target.value)"
              >
                <option
                  v-for="stage in getStagesForDeal(deal)"
                  :key="stage.id"
                  :value="stage.id"
                >
                  {{ stage.name }}
                </option>
              </select>
            </div>

            <!-- Dates -->
            <div
              class="flex items-center gap-3 text-[10px] text-n-slate-10 mb-2"
            >
              <div class="flex items-center gap-1">
                <span class="i-lucide-calendar-plus size-2.5" />{{
                  formatDate(deal.created_at)
                }}
              </div>
              <div class="flex items-center gap-1">
                <span class="i-lucide-calendar-check size-2.5" />{{
                  formatDate(deal.expected_close_date)
                }}
              </div>
            </div>

            <!-- Action Buttons -->
            <div v-if="deal.status === 'open'" class="flex gap-1.5">
              <button
                class="flex-1 flex items-center justify-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/40 transition-colors"
                @click="markWon(deal)"
              >
                <span class="i-lucide-check size-3" />{{
                  t('KANBAN.DEAL_MODAL.BTN_WON')
                }}
              </button>
              <button
                class="flex-1 flex items-center justify-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors"
                @click="markLost(deal)"
              >
                <span class="i-lucide-x size-3" />{{
                  t('KANBAN.DEAL_MODAL.BTN_LOST')
                }}
              </button>
            </div>

            <!-- Status Badge -->
            <div
              v-else
              class="flex items-center justify-center gap-1.5 py-1 rounded-md"
              :class="
                deal.status === 'won'
                  ? 'bg-emerald-50 dark:bg-emerald-900/10'
                  : 'bg-red-50 dark:bg-red-900/10'
              "
            >
              <span
                :class="[
                  (statusConfig[deal.status] || statusConfig.open).icon,
                  (statusConfig[deal.status] || statusConfig.open).class,
                ]"
                class="size-3"
              />
              <span
                class="text-[10px] font-semibold"
                :class="
                  deal.status === 'won'
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-red-700 dark:text-red-400'
                "
              >
                {{ t((statusConfig[deal.status] || statusConfig.open).key) }}
              </span>
              <span
                v-if="deal.lost_reason"
                class="text-[9px] text-red-600/70 dark:text-red-400/70 ml-1"
                >— {{ deal.lost_reason }}</span
              >
            </div>
          </div>

          <!-- Add deal to this pipeline -->
          <div class="px-3 py-1.5 border-t border-n-weak/30">
            <button
              class="inline-flex items-center gap-1 text-[10px] text-n-slate-10 hover:text-n-brand transition-colors"
              @click="openCreateForm(group.pipeline?.id)"
            >
              <span class="i-lucide-plus size-2.5" />{{
                t('KANBAN.SIDEBAR.ADD_TO_PIPELINE')
              }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
